import React, { useState, useEffect, useRef, useMemo } from 'react';

// ─────────────────────────────────────────────────────────────────────────────
// Shared "Customize Columns" gear popover — one design across every list module
// (Services, Clients, Tasks, CRM, Finance). Show/hide + reorder (drag OR ▲▼
// arrows), persisted to localStorage, with a Reset to default.
//
// Usage:
//   const cc = useColumnCustomizer(MY_COLUMNS, {
//     visKey: 'taskVisibleCols', orderKey: 'taskColumnOrder', T, darkMode,
//   });
//   // header cells:  {cc.orderedVisibleKeys.map(key => <th key={key}>{label(key)}</th>)}
//   // body cells:    {cc.orderedVisibleKeys.map(key => <td key={key}>{renderCell(row, key)}</td>)}
//   // gear cell:     <th ...>{cc.menu}</th>
//   // colSpan for empty rows: cc.visibleColumns.length + <fixed extra columns>
//
// MY_COLUMNS = [{ key, label, default? }]. If no column carries `default`, the
// default view is "all visible".
// ─────────────────────────────────────────────────────────────────────────────

export function useColumnCustomizer(columns, { visKey, orderKey, T, darkMode = false }) {
  const allKeys = useMemo(() => columns.map(c => c.key), [columns]);
  const defaultVisible = useMemo(
    () => (columns.some(c => c.default) ? columns.filter(c => c.default).map(c => c.key) : allKeys),
    [columns, allKeys]
  );

  // Keep a stored order but drop unknown keys and append any newly-added columns,
  // so saved prefs survive a change to the column list.
  const reconcileOrder = (stored) => {
    const valid = (Array.isArray(stored) ? stored : []).filter(k => allKeys.includes(k));
    return valid.length ? [...valid, ...allKeys.filter(k => !valid.includes(k))] : allKeys;
  };
  const reconcileVisible = (stored) => {
    if (!Array.isArray(stored)) return defaultVisible;
    const valid = stored.filter(k => allKeys.includes(k));
    return valid; // may legitimately be empty if the user hid everything
  };

  const [visible, setVisible] = useState(() => {
    try { return reconcileVisible(JSON.parse(localStorage.getItem(visKey))); } catch { return defaultVisible; }
  });
  const [order, setOrder] = useState(() => {
    try { return reconcileOrder(JSON.parse(localStorage.getItem(orderKey))); } catch { return allKeys; }
  });
  const [open, setOpen] = useState(false);
  const [dragCol, setDragCol] = useState(null);
  const ref = useRef(null);

  useEffect(() => {
    try {
      localStorage.setItem(visKey, JSON.stringify(visible));
      localStorage.setItem(orderKey, JSON.stringify(order));
    } catch { /* private mode / quota — ignore */ }
  }, [visible, order, visKey, orderKey]);

  useEffect(() => {
    if (!open) return;
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, [open]);

  const toggle = (key) => setVisible(v => (v.includes(key) ? v.filter(k => k !== key) : [...v, key]));

  const move = (key, dir) => setOrder(prev => {
    const from = prev.indexOf(key);
    const to = from + dir;
    if (from < 0 || to < 0 || to >= prev.length) return prev;
    const next = [...prev];
    [next[from], next[to]] = [next[to], next[from]];
    return next;
  });

  const onDragStart = (e, key) => {
    setDragCol(key);
    e.dataTransfer.effectAllowed = 'move';
    try { e.dataTransfer.setData('text/plain', key); } catch { /* IE guard */ }
  };
  const onDragEnter = (key) => {
    if (!dragCol || dragCol === key) return;
    setOrder(prev => {
      const from = prev.indexOf(dragCol);
      const to = prev.indexOf(key);
      if (from < 0 || to < 0) return prev;
      const next = [...prev];
      next.splice(from, 1);
      next.splice(to, 0, dragCol);
      return next;
    });
  };

  const reset = () => { setVisible(defaultVisible); setOrder(allKeys); };

  const orderedVisibleKeys = order.filter(k => visible.includes(k));

  const menu = (
    <div style={{ position: 'relative', display: 'inline-block' }} ref={ref}>
      <button type="button" onClick={(e) => { e.stopPropagation(); setOpen(o => !o); }} title="Customize Columns"
        style={{ width: 32, height: 32, borderRadius: 8, border: open ? '1px solid rgba(255,255,255,0.6)' : '1px solid rgba(255,255,255,0.25)', background: open ? 'rgba(255,255,255,0.2)' : 'rgba(255,255,255,0.1)', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}>
        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="12" cy="12" r="3" />
          <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
        </svg>
      </button>

      {open && (
        <div onClick={e => e.stopPropagation()}
          style={{ position: 'absolute', right: 0, top: 40, background: T.bg2, border: `1px solid ${T.border2}`, borderRadius: 12, boxShadow: darkMode ? '0 12px 40px rgba(0,0,0,0.5)' : '0 12px 40px rgba(15,31,69,0.12)', zIndex: 999, width: 264, padding: 16, textAlign: 'left' }}>
          <p style={{ fontSize: 13, fontWeight: 700, color: T.text, margin: '0 0 4px' }}>Customize Columns</p>
          <p style={{ fontSize: 11, color: T.text2, margin: '0 0 12px' }}>Drag or use arrows to reorder · Toggle to show/hide</p>

          <div style={{ maxHeight: 240, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 2, scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
            {order.map((key, i) => {
              const col = columns.find(c => c.key === key);
              if (!col) return null;
              return (
                <div key={key} draggable
                  onDragStart={e => onDragStart(e, key)}
                  onDragEnter={() => onDragEnter(key)}
                  onDragOver={e => e.preventDefault()}
                  onDragEnd={() => setDragCol(null)}
                  style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 8px', borderRadius: 8, cursor: 'grab', userSelect: 'none', background: dragCol === key ? T.bg3 : 'transparent' }}
                  onMouseEnter={e => { e.currentTarget.style.background = T.bg3; }}
                  onMouseLeave={e => { e.currentTarget.style.background = dragCol === key ? T.bg3 : 'transparent'; }}>
                  <svg width="12" height="12" viewBox="0 0 14 14" fill={darkMode ? T.text2 : T.text3} style={{ flexShrink: 0 }}>
                    <circle cx="3" cy="3" r="1.5" /><circle cx="7" cy="3" r="1.5" /><circle cx="11" cy="3" r="1.5" />
                    <circle cx="3" cy="7" r="1.5" /><circle cx="7" cy="7" r="1.5" /><circle cx="11" cy="7" r="1.5" />
                  </svg>
                  <input type="checkbox" checked={visible.includes(key)} onChange={() => toggle(key)} />
                  <span style={{ fontSize: 13, color: T.text, flex: 1 }}>{col.label}</span>
                  <button type="button" onClick={() => move(key, -1)} disabled={i === 0} title="Move up"
                    style={{ border: 'none', background: 'none', padding: 2, display: 'flex', cursor: i === 0 ? 'default' : 'pointer', color: i === 0 ? T.text3 : T.text2, opacity: i === 0 ? 0.3 : 1 }}>
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="18 15 12 9 6 15" /></svg>
                  </button>
                  <button type="button" onClick={() => move(key, 1)} disabled={i === order.length - 1} title="Move down"
                    style={{ border: 'none', background: 'none', padding: 2, display: 'flex', cursor: i === order.length - 1 ? 'default' : 'pointer', color: i === order.length - 1 ? T.text3 : T.text2, opacity: i === order.length - 1 ? 0.3 : 1 }}>
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="6 9 12 15 18 9" /></svg>
                  </button>
                </div>
              );
            })}
          </div>

          <button type="button" onClick={reset}
            style={{ marginTop: 12, width: '100%', padding: '7px 0', borderRadius: 8, border: `1px solid ${T.border2}`, background: T.bg3, color: T.text2, fontSize: 12, cursor: 'pointer' }}>
            Reset to default
          </button>
        </div>
      )}
    </div>
  );

  return { visibleColumns: visible, columnOrder: order, orderedVisibleKeys, menu, setOpen };
}
